import React, { Component } from 'react'
import WithClickOutside from 'react-click-outside'
import { get, debounce, find } from 'lodash'
import { compose, withApollo } from 'react-apollo'
import { withRouter } from 'react-router'
import styled, { css } from 'styled-components'
import { SearchAllPatients, CreateOrUpdatePatient } from '@sukiai/gql/app'
import { Search } from '@sukiai/components'
import { getFullName } from '@sukiai/utils'
import { LO_STO, SESH_STO } from '../../lib'
import { getAgeAndGender, isCerner, isAmwellTheme } from '../../lib/util'
import loSto from '../../config/loSto'
import seshSto from '../../config/seshSto'

const SearchResultContainer = styled.div`
  padding: 5px;  
  min-height: 50px;
  width: 100%;
  ${props => props.isAmwellTheme && css`
    height: 50px;
    cursor: pointer;

    &:hover {
      background: rgb(51, 57, 63)
    }
  `}
`

const ResultNameHolder = styled.div`
  font-weight: 600;
  font-size: 16px;
`

const ResultAdditionalContent = styled.div`
  font-size: 12px;
`

const NewPatientCreationHolder = styled.div`
  line-height: 40px;
  font-weight: 600;
  font-size: 16px;
  flex-direction: row;
  display: flex;
  flex-wrap: wrap;

  /* smaller space between elemenets */
  div:first-of-type {
    height: 25px;
    margin-right: 5px;
  }
`

const CREATE_NEW_PATIENT = 'Create New Patient'

class PatientSearch extends Component {
  constructor (props) {
    super(props)
    this.state = {
      patients: null,
      text: props.text || ''
    }
    this.search = this.search.bind(this)
  }

  async componentWillReceiveProps (nextProps) {
    if (nextProps.text && nextProps.text !== this.state.text) {
      this.setState({ text: nextProps.text })
    }
  }

  handleClickOutside = () => {
    this.setState({ isDropDownVisible: false })
  }

  searchPatient = (text) => {
    // helper function to set input value
    // search patient(debounced)
    this.setState({ text })
    this.search(text)
  }

  search = debounce(text => {
    if (text.length > 0) {
      const { client } = this.props
      client.query({
        query: SearchAllPatients,
        variables: { name: text }
      }).then(({ data }) => {
        const results = this.transformData(get(data, 'searchPatients.results'))

        // If its a non emr organization only
        // then give the user an option to create the patient
        // ALLOW CREATE A NEW PATIENT FOR CERNER USERS
        /*
          Need this for Medstar ED doctors who want to leverage non-integrated Suki for quick use
        */
        if (isCerner() || !seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.emr?.id) {
          const addPatientResult = {
            content: this.getCreateNewPatientResult(text)
          }
          results.push(addPatientResult)
        }
        this.setState({
          patients: results,
          isDropDownVisible: true
        })
      }).catch(err => {
        this.setState({
          patients: null,
          isDropDownVisible: false
        })
        console.error('Error searching patients:', err)
      })
    } else {
      this.setState({
        patients: null,
        isDropDownVisible: false
      })
    }
  }, 200)

  getCreateNewPatientResult = name => (
    <SearchResultContainer>
      <NewPatientCreationHolder data-cy='create-new-patient-search-result'>
        <div>{CREATE_NEW_PATIENT}</div>
        <div>"{name}"</div>
      </NewPatientCreationHolder>
    </SearchResultContainer>
  )

  transformData = data => {
    const patients = data.map(patient => ({
      ...patient,
      content: this.getSearchResultContent(getFullName(get(patient, 'person', {})), getAgeAndGender(get(patient, 'person', {})))
    }))
    return patients
  }

  getSearchResultContent = (name, additionalContent) => {
    return (
      <SearchResultContainer isAmwellTheme={isAmwellTheme()}>
        <ResultNameHolder data-cy='search-result-name'> {name} </ResultNameHolder>
        <ResultAdditionalContent data-cy='search-result-additional-content'> {additionalContent} </ResultAdditionalContent>
      </SearchResultContainer>
    )
  }

  goToPatient = id => {
    const { patients } = this.state
    const { handleUpdateComposition, client } = this.props

    if (id) {
      const currentPatientInfo = find(patients, patient => id === patient.id)
      delete currentPatientInfo.content
      handleUpdateComposition(currentPatientInfo)
      loSto.set(LO_STO.CURRENT_PATIENT_ID, id)
      this.setState({ text: getFullName(get(currentPatientInfo, 'person', {})) })
    } else {
      // TODO: Managing the error handling when no lastname is given or any other errors
      // TODO: Make it obvious to the user that firstName and lastName is mandatory
      const nameList = this.state.text.split(' ')
      client.mutate({
        mutation: CreateOrUpdatePatient,
        variables: {
          person: {
            firstName: nameList[0],
            middleName: nameList[2] ? nameList[1] : '',
            lastName: nameList[2] || nameList[1]
          }
        }
      }).then(({ data }) => {
        console.info('Created patient successfully')
        loSto.set(LO_STO.CURRENT_PATIENT_ID, get(data, 'createOrUpdatePatient.patient.id'))
        handleUpdateComposition(get(data, 'createOrUpdatePatient.patient'), true)
      }).catch(err => {
        console.error('Error searching patients:', err)
      })
    }
  }

  render () {
    const {
      patients,
      text,
      isDropDownVisible
    } = this.state

    const {
      isFocused,
      isDisabled
    } = this.props
    return (
      <Search
        search={this.searchPatient}
        handleSelection={this.goToPatient}
        resultsData={patients}
        searchText={text}
        isFocused={isFocused}
        isDisabled={isDisabled}
        isDropDownVisible={isDropDownVisible}
      />
    )
  }
}

export default compose(
  withRouter,
  withApollo
)(WithClickOutside(PatientSearch))
